import React from 'react';
import PropTypes from 'prop-types';
import { path } from 'ramda';
import { Redirect } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { useManagedMutation, useManagedQuery } from 'utils/managedHooks';
import { historyShape } from 'constants/propTypes';
import { Page, Row, Column } from 'components/shared/Layout';
import { ConfirmButton, CancelButton } from 'components/ui/Button';
import { GET_BOOKING } from 'store/queries';
import { CONFIRM_BOOKING, REVERT_CHANGE } from 'store/mutations';
import { FullPageSpinner } from 'admin-components/components';
import { getBookingJourneysURL, getRefundMethodsURL, getPendingPaymentURL } from 'utils/urls';
import ReviewExtrasSummary from './ReviewExtrasSummary';
import ReviewExtrasCost from './ReviewExtrasCost';

const ReviewBookingExtras = ({ history, match }) => {
  const { reference } = match.params;
  const membershipNumber = new URLSearchParams(history?.location?.search).get('membershipNumber');

  const { loading: bookingLoading, data } = useManagedQuery(GET_BOOKING, {
    variables: { reference },
    fetchPolicy: 'network-only'
  });

  const [confirmBooking, { loading: confirmBookingLoading }] = useManagedMutation(CONFIRM_BOOKING);
  const [revertChange, { loading: revertChangeLoading }] = useManagedMutation(REVERT_CHANGE);

  if (bookingLoading) {
    return <FullPageSpinner />;
  }

  const booking = path(['booking'], data);

  const { currency, totalPriceToBePaid, outboundExtras, inboundExtras } = booking;

  const cancelledOutboundExtras = outboundExtras.filter((extra) => extra.cancelled && extra.provisional);
  const newOutboundExtras = outboundExtras.filter((extra) => !extra.cancelled && extra.provisional);
  const cancelledInboundExtras = inboundExtras.filter((extra) => extra.cancelled && extra.provisional);
  const newInboundExtras = inboundExtras.filter((extra) => !extra.cancelled && extra.provisional);

  if (!cancelledOutboundExtras.length && !newOutboundExtras.length && !cancelledInboundExtras.length && !newInboundExtras.length) {
    return <Redirect to={getBookingJourneysURL(reference, membershipNumber)} />;
  }

  const revertExtrasChanges = async () => {
    await revertChange({ variables: { bookingReference: reference } });
    history.push(getBookingJourneysURL(reference, membershipNumber));
  };

  const confirmExtras = async () => {
    if (totalPriceToBePaid > 0) {
      return history.push(getPendingPaymentURL(reference, currency, membershipNumber));
    }
    if (totalPriceToBePaid <= 0) {
      await confirmBooking({
        variables: {
          bookingReference: reference
        }
      });
    }
    if (totalPriceToBePaid < 0) {
      return history.push(getRefundMethodsURL(reference, currency, membershipNumber));
    }

    return history.push(getBookingJourneysURL(reference, membershipNumber));
  };

  const getConfirmButtonLabel = () => {
    if (totalPriceToBePaid > 0) {
      return 'exchangeConfirmation.takePayment';
    }
    if (totalPriceToBePaid < 0) {
      return 'payments.makeRefund';
    }

    return 'extras.confirm';
  };

  return (
    <Page>
      <Row start={2} span={14}>
        <h1>
          <FormattedMessage id="extras.reviewExtras" />
        </h1>
        <ReviewExtrasSummary title="extras.extrasSummary" booking={booking} />
        <ReviewExtrasCost title="extras.costOfChangingExtras" booking={booking} />
      </Row>
      <Row span={14} start={2}>
        <Column span={4}>
          <ConfirmButton
            data-cy="confirm-button"
            data-testid="confirm-button"
            onClick={confirmExtras}
            loading={confirmBookingLoading}
            disabled={confirmBookingLoading || revertChangeLoading}
            variant="primary"
          >
            <FormattedMessage id={getConfirmButtonLabel()} />
          </ConfirmButton>
        </Column>
        <Column span={4}>
          <CancelButton
            data-testid="revert-button"
            onClick={revertExtrasChanges}
            loading={revertChangeLoading}
            disabled={confirmBookingLoading || revertChangeLoading}
          >
            <FormattedMessage id="extras.cancel" />
          </CancelButton>
        </Column>
      </Row>
    </Page>
  );
};

ReviewBookingExtras.propTypes = {
  history: historyShape,
  match: PropTypes.shape({
    params: PropTypes.shape({
      reference: PropTypes.string
    })
  })
};

export default ReviewBookingExtras;
